import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {Nav, NavItem, NavLink, TabContent, TabPane} from 'reactstrap';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import Translate from '@src/components/Translate/Translate';
import OrderProductsExtraRow from '@views/orders/fields/products/extra_row/OrderProductsExtraRow';
import {default as OperationsBody} from '@views/orders/fields/products/extra_row/operations/LazyBody';
import {default as MaterialsBody} from '@views/orders/fields/products/extra_row/materials/LazyBody';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import {shortCode} from '@src/helpers/hash/shortCode';
import LazyBody from '@views/orders/fields/products/extra_row/tech-process/LazyBody';
import TechProcessTabVisibilityManager
    from '@views/orders/fields/products/extra_row/visibility-managers/TechProcessTabVisibilityManager';
import TabsControlsWrapper from '@views/orders/fields/products/extra_row/TabsControlsWrapper';
import {IOrderProductModel} from '@src/@interfaces/models/Order/IOrderProductModel';
import {useFormContext} from 'react-hook-form';
import OrderProductChecklistsTabManager from '@views/orders/fields/products/extra_row/OrderProductChecklistsTabManager';

type Props<Model extends IEmptyModel> = React.ComponentProps<(typeof OrderProductsExtraRow<Model>)>;

enum tabsNames {
    INFO = 'OrderForm.products.tabs.info',
    TECH_PROCESS = 'OrderForm.products.tabs.tech-process',
    OPERATIONS = 'OrderForm.products.tabs.operations',
    MATERIALS = 'OrderForm.products.tabs.materials',
    CHECKLIST = 'OrderForm.products.tabs.checklist',
}

const tabs: string[] = [
    tabsNames.INFO,
    tabsNames.TECH_PROCESS,
    tabsNames.OPERATIONS,
    tabsNames.MATERIALS,
    tabsNames.CHECKLIST,
];

const changeMap = (map: Record<string, true>, key: string, shouldPushRecord: boolean): Record<string, true> => {
    const newMap = {...map};
    if (shouldPushRecord) {
        newMap[key] = true;
    } else {
        delete newMap[key];
    }

    return newMap;
};

const OrderProductsExtraRowMemoized = React.memo(OrderProductsExtraRow);

export default function ExtraRowTabs<Model extends IEmptyModel>(props: Pick<Props<Model>, keyof Props<Model>>) {
    const TAB_NAME_GET_PARAM_NAME = shortCode('OrderForm.products');

    const uniqueCode = props.rowId;
    const prepareTabName = (name: string | null): string => `${uniqueCode}--${name}`;

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const locationTab = searchParams.get(TAB_NAME_GET_PARAM_NAME) ?? '';
    const [activeTab, setActiveTab] = useState(
            tabs.map(prepareTabName).includes(locationTab) ?
                    locationTab :
                    prepareTabName(tabs[0])
    );
    const [loadedTabs, setLoadedTabs] = useState<Record<string, true>>({[activeTab]: true});
    const [hiddenTabs, setHiddenTabs] = useState<Record<string, true>>({});

    const onNavLinkClick = useMemo(() => {
        const makeListener = (tabName: string): React.MouseEventHandler => e => {
            e.preventDefault();

            const preparedTabName = `${uniqueCode}--${tabName}`;
            setActiveTab(preparedTabName);
            setLoadedTabs(map => {
                if (preparedTabName in map) {
                    return map;
                }

                return ({...map, [preparedTabName]: true});
            });

            searchParams.set(TAB_NAME_GET_PARAM_NAME, preparedTabName);

            navigate({
                ...location,
                search: searchParams.toString(),
            });
        };

        return tabs.map(name => makeListener(name));
    }, [TAB_NAME_GET_PARAM_NAME, location, navigate, searchParams, uniqueCode]);

    const canSeeOperations = usePermissionGranted('orders@can-see-product-operations');
    const canSeeChecklists = usePermissionGranted('orders@can-see-product-checklist');
    const canSeeMaterials = usePermissionGranted('orders@can-see-product-materials');

    const [toggleTabVisibility, tabsVisibilityChangers] = useMemo(() => {
        const toggleTabVisibility = (name: string, shouldBeVisible: boolean) => {
            setHiddenTabs(map => changeMap(map, name, !shouldBeVisible));
        };

        const tabsVisibilityChangers = tabs.reduce<Record<string, (shouldBeVisible: boolean) => void>>((res, tabName) => {
            res[tabName] = (shouldBeVisible: boolean) => toggleTabVisibility(tabName, shouldBeVisible);

            return res;
        }, {});

        return [toggleTabVisibility, tabsVisibilityChangers];
    }, []);

    useEffect(() => {
        setHiddenTabs(map => {
            return [
                {
                    shouldHide: !canSeeOperations,
                    name: tabsNames.OPERATIONS
                }, {
                    shouldHide: tabsNames.CHECKLIST in map ? map[tabsNames.CHECKLIST] : !canSeeChecklists,
                    name: tabsNames.CHECKLIST,
                }, {
                    shouldHide: !canSeeMaterials,
                    name: tabsNames.MATERIALS,
                }
            ].reduce((res, item) => {
                return changeMap(res, item.name, item.shouldHide);
            }, map);
        });
    }, [canSeeChecklists, canSeeMaterials, canSeeOperations]);

    const productNamePrefix = `products.${props.rowIndex}`;
    const {setValue} = useFormContext();
    const onIgnoreProductFlagChanged = useCallback((state: boolean) => {
        setValue(`${productNamePrefix}.has_to_be_ignored_by_order_price`, state);
    }, [productNamePrefix, setValue]);

    return <TechProcessTabVisibilityManager
            tabName={tabsNames.TECH_PROCESS}
            toggleTabVisibility={toggleTabVisibility}
            productNamePrefix={productNamePrefix}
    >
        <TabsControlsWrapper
                className="nav-tabs-custom"
                product={props.rowRecord as unknown as IOrderProductModel}
                onChange={onIgnoreProductFlagChanged}
        >
            <Nav className="border-bottom-0" tabs>
                {tabs.map((name, i) => {
                    if (name in hiddenTabs) {
                        return null;
                    }

                    const label = name;
                    name = prepareTabName(name);

                    return <NavItem key={`OrderForm-${name}-tab-control`}>
                        <NavLink href={`#${name}`} active={activeTab === name} onClick={onNavLinkClick[i]}>
                            <Translate message={label}/>
                        </NavLink>
                    </NavItem>;
                })}
            </Nav>
        </TabsControlsWrapper>

        <TabContent activeTab={activeTab} className="py-3">
            {tabs.map((tabName) => {
                const name = prepareTabName(tabName);
                if (tabName in hiddenTabs || !(name in loadedTabs) && tabName !== tabsNames.CHECKLIST) {
                    return null;
                }

                return <TabPane tabId={name} key={`OrderForm-${name}-tab-pane`}>
                    {tabName === tabsNames.INFO ? <OrderProductsExtraRowMemoized {...props}/> : null}

                    {tabName === tabsNames.TECH_PROCESS && (props.rowRecord.id && props.model.id) ? <LazyBody
                            namePrefix={productNamePrefix}
                            orderId={props.model.id}
                            productId={props.rowRecord.id}
                            controllerName={props.controllerName ?? 'Orders'}
                            forceDisabled={props.isDisabled}
                    /> : null}

                    {tabName === tabsNames.OPERATIONS && (props.rowRecord.id && props.model.id) ? (
                            <OperationsBody
                                    rowId={+props.rowId}
                                    rowIndex={props.rowIndex}
                                    orderId={props.model.id}
                                    productId={props.rowRecord.id}
                                    controllerName={props.controllerName ?? 'Orders'}
                                    forceDisabled={props.isDisabled}
                                    isDisplayed={props.collapsedRows?.[props.rowId] !== true}
                            />
                    ) : null}

                    {tabName === tabsNames.MATERIALS && (props.rowRecord.id && props.model.id) ? (
                            <MaterialsBody
                                    rowId={+props.rowId}
                                    rowIndex={props.rowIndex}
                                    orderId={props.model.id}
                                    productId={props.rowRecord.id}
                                    controllerName={props.controllerName ?? 'Orders'}
                                    forceDisabled={props.isDisabled}
                                    isDisplayed={props.collapsedRows?.[props.rowId] !== true}
                            />
                    ) : null}

                    {(tabName === tabsNames.CHECKLIST && props.model.products?.[props.rowIndex]?.status_id) ? (
                            <OrderProductChecklistsTabManager
                                    {...props}
                                    entityStatusId={props.model.products?.[props.rowIndex]?.status_id}
                                    checkedItems={props.model.products?.[props.rowIndex]?.checked_checklists_items}
                                    toggleTabVisibility={tabsVisibilityChangers[tabsNames.CHECKLIST]}
                                    display={name in loadedTabs}
                            />
                    ) : null}
                </TabPane>;
            })}
        </TabContent>
    </TechProcessTabVisibilityManager>;
}
